// ChatManagement.jsx — landing for the Chat Support Management package.
// Lives at /chat-management/:tab? — deep-linkable. Phase 2 wired:
//
//   - Inbox row click → conversation viewer with message list +
//     approve/launch button on AI-drafted replies.
//   - Channel create/edit form covering type, widgetKey, autoReply
//     gates, and the intent vocabulary that the classifier consumes.
//   - Knowledge editor (create/edit/delete) with manual reindex action;
//     POST/PUT both emit CHAT_KB_INDEX_REQUESTED automatically so the
//     chunker re-runs.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { get, post, putGeneral } from "../../api";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import SystemFilterBar from "components/SystemFilterBar";
import { applySystemFilter, EMPTY_SYSTEM_FILTERS } from "../../helpers/systemFilters";
import useBrandList from "../../hooks/useBrandList";

const TABS = [
  { key: 'inbox',     label: 'Inbox',     endpoint: '/api/chat/conversations', perm: 'CHAT_CONVERSATION_VIEW' },
  { key: 'archive',   label: 'Archive',   endpoint: '/api/chat/conversations?archived=true', perm: 'CHAT_CONVERSATION_VIEW' },
  { key: 'channels',  label: 'Channels',  endpoint: '/api/chat/channels',      perm: 'CHAT_CHANNEL_VIEW' },
  { key: 'knowledge', label: 'Knowledge', endpoint: '/api/chat/knowledge',     perm: 'CHAT_KNOWLEDGE_VIEW' },
];

const INBOX_COLS = [['brand_id', 'Brand'], ['visitorEmail', 'From'], ['lastMessagePreview', 'Last message'], ['status', 'Status'], ['audience', 'Audience'], ['intentLabel', 'Intent'], ['sentiment', 'Sentiment'], ['createdAt', 'Started'], ['lastMessageAt', 'Last activity']];
const COLUMNS = {
  inbox:     INBOX_COLS,
  archive:   INBOX_COLS,
  channels:  [['name', 'Name'], ['type', 'Type'], ['autoReplyEnabled', 'Auto-reply'], ['widgetKey', 'Widget key']],
  knowledge: [['title', 'Title'], ['active', 'Active'], ['chunks.length', 'Chunks'], ['lastIndexedAt', 'Indexed'], ['updatedAt', 'Updated']],
};

function getPath(obj, path) { return path.split('.').reduce((o, k) => (o == null ? null : o[k]), obj); }
function fmtCell(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) { try { return new Date(v).toLocaleString(); } catch {} }
  if (typeof v === 'string' && v.length > 60) return v.slice(0, 60) + '…';
  return String(v);
}
function statusBadge(status) {
  const map = {
    open:                  { bg: '#eef2ff', fg: '#15172b' },
    'waiting-for-agent':   { bg: '#fff4cc', fg: '#856404' },
    'waiting-for-customer':{ bg: '#e7f5ec', fg: '#1e6b3e' },
    'closed-resolved':     { bg: '#fafbfc', fg: '#888' },
    'closed-unresolved':   { bg: '#fdecea', fg: '#a71d2a' },
  };
  const s = map[status] || { bg: '#fafbfc', fg: '#525252' };
  return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, background: s.bg, color: s.fg }}>{status || '—'}</span>;
}
function sentimentBadge(sent) {
  if (!sent) return '—';
  const colors = { positive: '#1e6b3e', neutral: '#525252', negative: '#a71d2a', mixed: '#856404' };
  return <span style={{ color: colors[sent] || '#525252', fontWeight: 600, fontSize: 11 }}>{sent}</span>;
}

// ────────────────────────────────────────────────────────────── Modal
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: wide ? 760 : 560, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0" style={{ wordBreak: 'break-word', paddingRight: 12 }}>{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────── Conversation viewer
function ConversationViewer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [reply, setReply] = useState('');
  const isSuperAdmin = useSelector((s) => s.user.userInfo?.superAdmin === true);
  const viewerBrands = useBrandList();

  const refresh = useCallback(async () => {
    try {
      const r = await get(`/api/chat/conversations/${id}`);
      setData((r && r.data) || null);
    } catch (e) { setErr((e && e.message) || 'Load failed'); }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const launchDraft = async (msg) => {
    if (!window.confirm('Approve + send this AI-drafted reply?')) return;
    setBusy(true);
    try {
      // Reuse the conversation/:id/reply endpoint to send a final message
      // containing the draft body. Hides the original draft as 'sent'.
      await post(`/api/chat/conversations/${id}/reply`, { bodyText: msg.bodyText }, false);
      await refresh();
      onChanged && onChanged();
    } catch (e) { alert((e && e.message) || 'Send failed'); }
    finally { setBusy(false); }
  };

  const sendManual = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await post(`/api/chat/conversations/${id}/reply`, { bodyText: reply }, false);
      setReply('');
      await refresh();
      onChanged && onChanged();
    } catch (e) { alert((e && e.message) || 'Send failed'); }
    finally { setBusy(false); }
  };

  const close = async (resolved) => {
    if (!window.confirm(`Mark conversation ${resolved ? 'resolved' : 'unresolved'} and close?`)) return;
    setBusy(true);
    try {
      await post(`/api/chat/conversations/${id}/close`, { resolved }, false);
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) { alert((e && e.message) || 'Close failed'); }
    finally { setBusy(false); }
  };

  // Take over (pause the bot for this thread) or hand back to the bot.
  const toggleBot = async () => {
    setBusy(true);
    try {
      await post(`/api/chat/conversations/${id}/bot`, { paused: !data.botPaused }, false);
      await refresh();
      onChanged && onChanged();
    } catch (e) { alert((e && e.message) || 'Toggle failed'); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <Modal title="Conversation" onClose={onClose} wide>{err ? <div className="alert alert-warning">{err}</div> : 'Loading…'}</Modal>;
  }

  return (
    <Modal title={`${data.visitorName || data.visitorEmail || data.visitorId || 'Visitor'} · ${data.status}`} onClose={onClose} wide>
      {/* Origin + visitor identity strip: which brand this chat came from,
          when it started, and who's on the other side. */}
      <div style={{ fontSize: 12, background: '#f6f8fb', border: '1px solid #e5e9f0', borderRadius: 6, padding: '8px 10px', marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}>
        <span><strong>Brand:</strong> {(viewerBrands || []).find((b) => String(b._id) === String(data.brand_id))?.name || data.brand_id || '—'}</span>
        <span><strong>Started:</strong> {data.createdAt ? new Date(data.createdAt).toLocaleString() : '—'}</span>
        <span><strong>Visitor:</strong> {data.visitorName || '—'}{data.visitorEmail ? ` · ${data.visitorEmail}` : ''}{data.visitorId ? ` · id:${String(data.visitorId).slice(0, 16)}` : ''}</span>
        {data.pageUrl ? <span><strong>Page:</strong> {String(data.pageUrl).slice(0, 60)}</span> : null}
      </div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
        Status: {statusBadge(data.status)} ·
        Intent: {data.intentLabel || '—'} · Sentiment: {sentimentBadge(data.sentiment)} ·
        Bot: {data.botPaused
          ? <span style={{ color: '#a71d2a', fontWeight: 600 }}>paused — you're handling</span>
          : <span style={{ color: '#1e6b3e', fontWeight: 600 }}>active</span>}
        {isSuperAdmin && (
          <> · AI cost: ${Number(data.aiCostUSD || 0).toFixed(4)}</>
        )}
      </div>

      <div style={{ maxHeight: '52vh', overflow: 'auto', background: '#fafbfc', borderRadius: 6, padding: 12 }}>
        {(data.messages || []).map((m) => (
          <div key={m._id} style={{
            display: 'flex',
            justifyContent: m.direction === 'inbound' ? 'flex-start' : 'flex-end',
            marginBottom: 10,
          }}>
            <div style={{
              maxWidth: '75%',
              background: m.direction === 'inbound' ? '#fff' : '#15172b',
              color: m.direction === 'inbound' ? '#15172b' : '#fff',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
              border: m.direction === 'inbound' ? '1px solid #e5e5e5' : 'none',
            }}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{m.bodyText || <em>(empty)</em>}</div>
              <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>
                {m.authorType}{m.intentLabel ? ` · ${m.intentLabel}` : ''} · {m.status} · {new Date(m.receivedAt || m.sentAt || m.createdAt || Date.now()).toLocaleString()}
              </div>
              {m.direction === 'outbound' && m.status === 'draft' && m.authorType === 'bot' && (
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={() => launchDraft(m)} disabled={busy}>Approve + send</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <textarea
          rows={2}
          className="form-control"
          placeholder="Reply manually…"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          style={{ flex: 1, fontSize: 13 }}
        />
        <button className="acc-btn acc-btn-primary" onClick={sendManual} disabled={busy || !reply.trim()}>Send</button>
      </div>

      {data.status && !data.status.startsWith('closed') && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="acc-btn" onClick={toggleBot} disabled={busy}
            style={data.botPaused ? { borderColor: '#1e6b3e', color: '#1e6b3e' } : { borderColor: '#a71d2a', color: '#a71d2a' }}>
            {data.botPaused ? '🤖 Hand back to bot' : '✋ Take over (pause bot)'}
          </button>
          <div style={{ flex: 1 }} />
          <button className="acc-btn" onClick={() => close(false)} disabled={busy}>Close unresolved</button>
          <button className="acc-btn" onClick={() => close(true)} disabled={busy}>Close resolved</button>
        </div>
      )}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── Install snippets
// Shown in the channel editor once a widgetKey exists (set by the
// auto-generation on POST /api/chat/channels). Three flavours so the
// user can paste straight into their stack.
function InstallSnippets({ widgetKey }) {
  const [tab, setTab] = useState('html');
  const [copied, setCopied] = useState(false);
  const origin = window.location.origin;
  const snippets = {
    html: `<!-- Prigmar chat widget -->
<script src="${origin}/widget.js"
        data-widget-key="${widgetKey}"
        async></script>`,
    react: `import { useEffect } from 'react';

// Drop into any component that mounts on every page (e.g. App.jsx).
// The widget self-injects into <body>; unmounting is a no-op.
export function PrigmarChat() {
  useEffect(() => {
    if (document.getElementById('prigmar-chat-widget')) return;
    const s = document.createElement('script');
    s.id  = 'prigmar-chat-widget';
    s.src = '${origin}/widget.js';
    s.setAttribute('data-widget-key', '${widgetKey}');
    s.async = true;
    document.body.appendChild(s);
  }, []);
  return null;
}`,
    nextjs: `// app/layout.tsx (App Router) or pages/_app.tsx (Pages Router)
import Script from 'next/script';

export default function RootLayout({ children }) {
  return (
    <html>
      <body>
        {children}
        <Script
          src="${origin}/widget.js"
          data-widget-key="${widgetKey}"
          strategy="afterInteractive"
        />
      </body>
    </html>
  );
}`,
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippets[tab]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked — user can still select + copy */ }
  };
  return (
    <div style={{ background: '#fafbfc', borderRadius: 6, padding: 10, marginTop: 12, fontSize: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontWeight: 600 }}>Install on your site — widget auto-generated</div>
        <button
          type="button"
          className="acc-btn"
          style={{ fontSize: 10, padding: '2px 8px' }}
          onClick={copy}
        >{copied ? 'Copied ✓' : 'Copy'}</button>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {[
          { k: 'html',   l: 'HTML' },
          { k: 'react',  l: 'React' },
          { k: 'nextjs', l: 'Next.js' },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            style={{
              padding: '3px 10px', fontSize: 11, background: tab === t.k ? '#15172b' : '#fff',
              color: tab === t.k ? '#fff' : '#525252',
              border: '1px solid #e5e5e5', borderRadius: 4, cursor: 'pointer',
            }}
          >{t.l}</button>
        ))}
      </div>
      <pre style={{ margin: 0, fontSize: 11, color: '#525252', background: '#fff', border: '1px solid #e5e5e5', padding: 10, borderRadius: 4, overflow: 'auto', maxHeight: 280, whiteSpace: 'pre' }}>
{snippets[tab]}
      </pre>
      <div style={{ color: '#888', marginTop: 6 }}>
        {tab === 'html'   && 'Paste before </body>. Vanilla HTML, no build step.'}
        {tab === 'react'  && 'Import + render <PrigmarChat /> once at the app root.'}
        {tab === 'nextjs' && 'Works with both App Router and Pages Router. Uses next/script.'}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────── Channel editor
function ChannelEditor({ initial, onClose, onSaved }) {
  const isEdit = !!(initial && initial._id);
  const [form, setForm] = useState({
    name: initial?.name || '',
    type: initial?.type || 'widget',
    widgetKey: initial?.widgetKey || '',
    autoReplyEnabled: initial?.autoReplyEnabled ?? false,
    // Schema field is `requireApproval`; the editor wording stays
    // "require approval" but the body key matches the schema below.
    requireApproval: initial?.requireApproval ?? true,
    autoReplyMinConfidence: initial?.autoReplyMinConfidence ?? 0.7,
    intentsText: JSON.stringify(initial?.config?.intents || [], null, 2),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name) return setErr('Name is required');
    let intents = [];
    try { intents = form.intentsText ? JSON.parse(form.intentsText) : []; }
    catch (e) { return setErr('Intents JSON: ' + e.message); }
    setBusy(true); setErr(null);
    const body = {
      name: form.name,
      type: form.type,
      autoReplyEnabled: form.autoReplyEnabled,
      requireApproval: form.requireApproval,
      autoReplyMinConfidence: Number(form.autoReplyMinConfidence) || 0.7,
      config: { intents },
    };
    // widgetKey is auto-generated server-side on create; we never
    // submit it from the form. Server PUT also strips it from the
    // whitelist so a stale value here can't overwrite.
    try {
      if (isEdit) {
        await putGeneral(`/api/chat/channels/${initial._id}`, body);
        onSaved && onSaved();
      } else {
        // POST returns { _id, widgetKey } — pass that back so the
        // parent can re-open the editor in edit mode so the install
        // snippets are immediately visible.
        const r = await post('/api/chat/channels', body, false);
        const created = (r && r.data) || {};
        onSaved && onSaved(created);
      }
    } catch (e) { setErr((e && (e.message || e?.response?.data?.error)) || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? 'Edit channel' : 'New channel'} onClose={onClose}>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Name *
        <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Website widget" />
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={{ fontSize: 12 }}>Type
          <select className="form-control" value={form.type} onChange={(e) => set('type', e.target.value)}>
            <option value="widget">widget</option>
            <option value="webhook">webhook</option>
            <option value="api">api</option>
          </select>
        </label>
        <label style={{ fontSize: 12 }}>Widget key
          <input className="form-control" value={form.widgetKey} readOnly placeholder="auto-generated on save" style={{ background: '#fafbfc' }} />
        </label>
      </div>
      {isEdit && form.widgetKey && (
        <div style={{ marginTop: 6, marginBottom: 8, textAlign: 'right' }}>
          <button
            type="button"
            className="acc-btn"
            style={{ fontSize: 11, padding: '2px 10px' }}
            onClick={async () => {
              if (!window.confirm('Rotate widget key? Any embedded widget will stop working until you paste the new snippet.')) return;
              try {
                const r = await post(`/api/chat/channels/${initial._id}/regenerate-key`, {}, false);
                if (r && r.data && r.data.widgetKey) set('widgetKey', r.data.widgetKey);
              } catch (e) { setErr((e && e.message) || 'Regenerate failed'); }
            }}
          >Rotate key</button>
        </div>
      )}

      <h6 style={{ marginTop: 14, fontSize: 13 }}>Auto-reply</h6>
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', fontSize: 12, marginBottom: 8 }}>
        <label><input type="checkbox" checked={form.autoReplyEnabled} onChange={(e) => set('autoReplyEnabled', e.target.checked)} /> Enabled</label>
        <label><input type="checkbox" checked={form.requireApproval} onChange={(e) => set('requireApproval', e.target.checked)} /> Require human approval</label>
        <label>Min confidence
          <input type="number" min="0" max="1" step="0.05" className="form-control form-control-sm d-inline-block ml-2" style={{ width: 80 }} value={form.autoReplyMinConfidence} onChange={(e) => set('autoReplyMinConfidence', e.target.value)} />
        </label>
      </div>

      {isEdit && form.widgetKey && <InstallSnippets widgetKey={form.widgetKey} />}

      <label style={{ fontSize: 12, display: 'block', marginTop: 12 }}>Intent vocab (JSON array)
        <textarea
          rows={6}
          className="form-control"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 }}
          value={form.intentsText}
          onChange={(e) => set('intentsText', e.target.value)}
          placeholder='[ {"label":"support","description":"asking for help"}, {"label":"sales","description":"interested in buying","autoReply":false} ]'
        />
        <div style={{ fontSize: 11, color: '#888' }}>
          Each entry: <code>{`{ label, description, autoReply?, minConfidence? }`}</code>. Drives the classifier
          and the auto-reply gate.
        </div>
      </label>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── Knowledge editor
function KbEditor({ initial, onClose, onSaved }) {
  const isEdit = !!(initial && initial._id);
  const [form, setForm] = useState({
    title: initial?.title || '',
    bodyMarkdown: initial?.bodyMarkdown || '',
    sourceUrl: initial?.sourceUrl || '',
    tags: (initial?.tags || []).join(', '),
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title) return setErr('Title is required');
    const body = {
      title: form.title,
      bodyMarkdown: form.bodyMarkdown,
      sourceUrl: form.sourceUrl,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      active: !!form.active,
    };
    setBusy(true); setErr(null);
    try {
      if (isEdit) await putGeneral(`/api/chat/knowledge/${initial._id}`, body);
      else        await post('/api/chat/knowledge', body, false);
      onSaved && onSaved();
    } catch (e) { setErr((e && (e.message || e?.response?.data?.error)) || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? 'Edit knowledge article' : 'New knowledge article'} onClose={onClose} wide>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Title *
        <input className="form-control" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="How to reset your password" />
      </label>
      <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>Body (markdown)
        <textarea
          rows={12}
          className="form-control"
          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
          value={form.bodyMarkdown}
          onChange={(e) => set('bodyMarkdown', e.target.value)}
        />
        <div style={{ fontSize: 11, color: '#888' }}>
          Save triggers re-indexing — the worker chunks this body into ~1600-char slices for retrieval.
        </div>
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
        <label style={{ fontSize: 12 }}>Source URL (optional)
          <input className="form-control" value={form.sourceUrl} onChange={(e) => set('sourceUrl', e.target.value)} />
        </label>
        <label style={{ fontSize: 12 }}>Tags
          <input className="form-control" value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="login, troubleshooting" />
        </label>
      </div>
      <label style={{ fontSize: 12, display: 'block', marginTop: 10 }}>
        <input type="checkbox" checked={!!form.active} onChange={(e) => set('active', e.target.checked)} /> Active (the assistant can pull from this)
      </label>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────── Page
export default function ChatManagement() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux   = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux     = useSelector((s) => s.user.allPermission);
  const FinalPermissions = useMemo(() => (
    (allPermissionRedux || [])
      .filter((p) => (companyPermissionRedux || []).includes(p))
      .filter((p) => (usersPermissionRedux || []).includes(p))
  ), [allPermissionRedux, companyPermissionRedux, usersPermissionRedux]);
  const isSuperAdmin = !!userInfoRedux?.superAdmin;
  const has = (perm) => isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS[perm]);

  const { tab: urlTab } = useParams();
  const history = useHistory();
  const visibleTabs = TABS.filter((t) => has(t.perm));
  const tab = (TABS.find((t) => t.key === urlTab) ? urlTab : (visibleTabs[0]?.key || 'inbox'));
  const setTab = (k) => history.push(`/chat-management/${k}`);

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [live, setLive] = useState(false); // inbox "active now" live mode
  // Header filter — brand + date (today / last week / last month / custom).
  const [filters, setFilters] = useState(EMPTY_SYSTEM_FILTERS);
  const brands = useBrandList();
  // Brand + date filtered rows. Declared before the early return below so the
  // hook order stays stable (rules-of-hooks).
  const visibleRows = useMemo(() => applySystemFilter(rows, filters), [rows, filters]);

  const activeTab = TABS.find((t) => t.key === tab);
  const refresh = useCallback(async () => {
    if (!activeTab) return;
    setBusy(true); setErr(null);
    try {
      const url = (tab === 'inbox' && live) ? activeTab.endpoint + '?active=1' : activeTab.endpoint;
      const r = await get(url);
      setRows(((r && r.data) || []));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); setRows([]); }
    finally { setBusy(false); }
  }, [activeTab, tab, live]);

  useEffect(() => { refresh(); }, [refresh]);
  // Auto-refresh: fast (5s) while watching live active chats, slow (30s) otherwise.
  useEffect(() => {
    const ms = (tab === 'inbox' && live) ? 5_000 : 30_000;
    const t = setInterval(() => refresh(), ms);
    return () => clearInterval(t);
  }, [refresh, tab, live]);

  if (visibleTabs.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          Your company doesn't have the Chat Support Management package enabled. Ask a super admin to add it.
        </div>
      </div>
    );
  }

  const cols = COLUMNS[tab] || [];
  const canAdd = (
    (tab === 'channels'  && has('CHAT_CHANNEL_ADD')) ||
    (tab === 'knowledge' && has('CHAT_KNOWLEDGE_ADD'))
  );
  const addLabel = tab === 'channels' ? 'channel' : tab === 'knowledge' ? 'article' : '';

  return (
    <div className="container py-4">
      <style>{`
        .cm-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e5e5e5; margin-bottom: 16px; }
        .cm-tab { padding: 8px 14px; cursor: pointer; border: 1px solid transparent; border-bottom: none; border-radius: 6px 6px 0 0; font-size: 13px; color: #525252; }
        .cm-tab.active { background: #fff; border-color: #e5e5e5; color: #15172b; font-weight: 600; }
        .cm-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; }
        .cm-table { width: 100%; font-size: 13px; }
        .cm-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .cm-table td { padding: 6px 8px; border-bottom: 1px solid #f4f4f4; }
        .cm-table tr.clickable { cursor: pointer; }
        .cm-table tr.clickable:hover { background: #fafbfc; }
        .cm-empty { color: #999; padding: 16px; text-align: center; }
        @keyframes cmpulse { 0% { opacity: 1; } 50% { opacity: .25; } 100% { opacity: 1; } }
        .cm-live-dot { display:inline-block; width:8px; height:8px; border-radius:4px; background:#1e6b3e; margin-right:6px; animation: cmpulse 1.2s infinite; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h3 className="mb-0">Chat Support</h3>
        {canAdd && (
          <button className="acc-btn acc-btn-primary" onClick={() => setEditing('new')}>+ New {addLabel}</button>
        )}
      </div>

      <div className="cm-tabs">
        {visibleTabs.map((t) => (
          <div key={t.key} className={"cm-tab" + (tab === t.key ? " active" : "")} onClick={() => setTab(t.key)}>{t.label}</div>
        ))}
      </div>

      <SystemFilterBar
        filters={filters}
        setFilters={setFilters}
        brands={brands}
        resultCount={visibleRows.length}
        totalCount={rows.length}
      />

      {tab === 'inbox' && (
        <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
          <button className="acc-btn" onClick={() => setLive((v) => !v)}
            style={live ? { borderColor: '#1e6b3e', color: '#1e6b3e' } : {}}>
            {live ? <><span className="cm-live-dot" />Live · active now</> : '● Show live (active now)'}
          </button>
          {live && <span className="f-12 text-muted">{rows.length} active chat{rows.length === 1 ? '' : 's'} · auto-refreshing every 5s</span>}
        </div>
      )}

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      <div className="cm-card">
        {busy && rows.length === 0 && <div className="cm-empty">Loading…</div>}
        {!busy && rows.length === 0 && <div className="cm-empty">No {activeTab.label.toLowerCase()} yet.</div>}
        {!busy && rows.length > 0 && visibleRows.length === 0 && <div className="cm-empty">No {activeTab.label.toLowerCase()} match the current filters.</div>}
        {visibleRows.length > 0 && (
          <table className="cm-table">
            <thead><tr>{cols.map(([key, label]) => (<th key={key}>{label}</th>))}<th /></tr></thead>
            <tbody>
              {visibleRows.map((r) => {
                const onRow = (tab === 'inbox' || tab === 'archive')
                  ? () => setConversationId(r._id)
                  : (tab === 'channels' || tab === 'knowledge') && (has('CHAT_CHANNEL_EDIT') || has('CHAT_KNOWLEDGE_EDIT'))
                  ? () => setEditing(r)
                  : null;
                return (
                  <tr key={r._id} className={onRow ? 'clickable' : ''} onClick={onRow || undefined}>
                    {cols.map(([key]) => (
                      <td key={key}>
                        {key === 'brand_id'
                          ? ((brands || []).find((b) => String(b._id) === String(r.brand_id))?.name || '—')
                          : key === 'status' && (tab === 'inbox' || tab === 'archive')
                          ? <>{statusBadge(r.status)}{r.botDrafting && <span style={{ marginLeft: 6, fontSize: 11, color: '#b45309' }}><span className="cm-live-dot" style={{ background: '#b45309' }} />typing…</span>}</>
                          : key === 'audience'
                          ? (r.audience === 'user'
                              ? <span style={{ fontSize: 11, color: '#1e6b3e', fontWeight: 600 }}>support</span>
                              : r.audience === 'prospect'
                              ? <span style={{ fontSize: 11, color: '#0F62FE', fontWeight: 600 }}>sales</span>
                              : <span style={{ color: '#bbb' }}>—</span>)
                          : key === 'sentiment'
                          ? sentimentBadge(r.sentiment)
                          : key === 'chunks.length'
                          ? (r.chunks || []).length
                          : fmtCell(getPath(r, key))}
                      </td>
                    ))}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {(tab === 'inbox' || tab === 'archive') && <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); setConversationId(r._id); }}>View</button>}
                      {tab === 'inbox' && (
                        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 6 }} title="Move to Archive"
                          onClick={async (e) => { e.stopPropagation(); try { await post(`/api/chat/conversations/${r._id}/archive`, { archived: true }, false); refresh(); } catch (err2) { alert('Archive failed'); } }}>
                          Archive
                        </button>
                      )}
                      {tab === 'archive' && (
                        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 6, borderColor: '#1e6b3e', color: '#1e6b3e' }} title="Restore to Inbox"
                          onClick={async (e) => { e.stopPropagation(); try { await post(`/api/chat/conversations/${r._id}/archive`, { archived: false }, false); refresh(); } catch (err2) { alert('Restore failed'); } }}>
                          Restore to Inbox
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && tab === 'channels' && (
        <ChannelEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            // After CREATE we keep the modal open + flip to edit mode
            // on the newly-created row, so the install snippets render
            // immediately for the user to copy. Edits close.
            if (created && created._id) {
              setEditing({ _id: created._id, widgetKey: created.widgetKey, name: '', type: 'widget' });
              refresh();
            } else {
              setEditing(null);
              refresh();
            }
          }}
        />
      )}
      {editing && tab === 'knowledge' && (
        <KbEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {conversationId && (
        <ConversationViewer
          id={conversationId}
          onClose={() => setConversationId(null)}
          onChanged={() => { refresh(); }}
        />
      )}
    </div>
  );
}
